interface Stage {
  id: string;
  label: string;
  comment: string;
  treeSortOrder: number;
  type: string;
  data: Record<string, any>;
}

interface CapabilityInstance {
  id: string;
  label: string;
  type: string;
  instanceOf: {
    id: string;
    label: string;
    type: string;
  }[];
  data: {
    statementTimeDimension: {
      id: string;
      label: string;
      type: string;
    }[];
  };
}

interface BusinessCapabilityStage {
  id: string;
  label: string;
  type: string;
  items: CapabilityInstance[];
}

interface Lane {
  id: string;
  label: string;
  type: string;
  treeSortOrder: number;
  stages: BusinessCapabilityStage[];
}

interface TransformedStage {
  id: string;
  label: string;
  description: string;
  sortOrder: number;
  type: string;
  // Performance metrics
  timePercentage: number;
  volumePercentage: number;
  qualityPercentage: number;
  sentimentPercentage: number;
  costPercentage: number;
  riskPercentage: number;
  healthPercentage: number;
  capabilities: {
    id: string;
    name: string;
    timeDimension: string;
    type: string;
  }[];
}

interface Capability {
  id: string;
  label: string;
  description: string;
  stageId: string;
  stageName: string;
  // Performance metrics
  timePercentage: number;
  volumePercentage: number;
  qualityPercentage: number;
  sentimentPercentage: number;
  costPercentage: number;
  riskPercentage: number;
  healthPercentage: number;
}

interface TransformedValueStream {
  valueStream: {
    id: string;
    label: string;
    description: string;
    type: string;
  };
  stages: TransformedStage[];
  capabilities: Capability[];
}

export interface ValueStreamResponse {
  valueStream: {
    id: string;
    label: string;
    description: string;
    type: string;
  };
  lanes?: Array<{
    id: string;
    label: string;
    type: string;
    treeSortOrder: number;
    stages: Array<{
      id: string;
      label: string;
      type: string;
      items: Array<{
        id: string;
        label: string;
        type: string;
        data?: {
          delegatedToOrganisation?: Array<{
            id: string;
            label: string;
            type: string;
          }>;
          deliveredByApplication?: Array<{
            id: string;
            label: string;
            type: string;
          }>;
          addressedBy?: Array<{
            id: string;
            label: string;
            type: string;
          }>;
          achievesValue?: Array<{
            id: string;
            label: string;
            type: string;
          }>;
          enactedBy?: Array<{
            id: string;
            label: string;
            type: string;
          }>;
        };
      }>;
    }>;
  }>;
  stages?: any;
  capabilities?: any;
}

interface ValueStreamDetails {
  valueStream: {
    id: string;
    label: string;
    description: string;
    type: string;
  };
  stages: {
    id: string;
    label: string;
    description: string;
    sortOrder: number;
  }[];
  capabilities: {
    id: string;
    label: string;
    description: string;
    stageId: string;
    stageName: string;
  }[];
}

export function transformValueStreamResponse(response: any): TransformedValueStream {
  // Validate response structure before transformation
  if (!response || typeof response !== 'object') {
    console.error('Invalid response format received:', response);
    // Return an empty structure to prevent errors
    return {
      valueStream: {
        id: '',
        label: 'Invalid Response',
        description: 'The API returned an invalid response format',
        type: 'unknown'
      },
      stages: [],
      capabilities: []
    };
  }

  // Log response structure for debugging
  console.debug('Processing response structure:', {
    hasValueStream: !!response.valueStream,
    stagesType: typeof response.stages,
    hasStagesItems: response.stages && Array.isArray(response.stages.items),
    hasLanes: Array.isArray(response.lanes),
    lanesLength: response.lanes ? response.lanes.length : 0
  });

  // Ensure value stream data exists and has the expected structure
  const valueStream = response.valueStream || {
    id: '',
    label: 'Unknown Value Stream',
    description: 'Value stream data is missing or incomplete',
    type: 'unknown'
  };

  // Transform value stream basic info
  const transformedValueStream: TransformedValueStream = {
    valueStream: {
      id: valueStream.id || '',
      label: valueStream.label || 'Unnamed Value Stream',
      description: valueStream.description || '',
      type: valueStream.type || 'valueStream',
    },
    stages: [],
    capabilities: []
  };

  // Extract stages from the correct location in the response
  // The API appears to return stages as an object with an 'items' array
  const stagesArray = 
    // Case 1: response.stages is an array directly
    Array.isArray(response.stages) ? response.stages : 
    // Case 2: response.stages is an object with items array property
    (response.stages && response.stages.items && Array.isArray(response.stages.items)) ? 
      response.stages.items : 
    // Case 3: no valid stages found
    [];

  if (stagesArray.length > 0) {
    // Transform stages
    transformedValueStream.stages = stagesArray.map((stage) => ({
      id: stage.id || '',
      label: stage.label || '',
      description: stage.description || '',
      sortOrder: stage.treeSortOrder || stage.sortOrder || 0,
      type: 'stage',
      // Add default performance metrics
      timePercentage: 0,
      volumePercentage: 0,
      qualityPercentage: 0,
      sentimentPercentage: 0,
      costPercentage: 0,
      riskPercentage: 0,
      healthPercentage: 0,
      capabilities: [] // Will populate from lanes if available
    }));
  } else {
    console.warn('No stages array found in the response:', response);
  }

  // Extract capabilities from lanes if available
  const allCapabilities: Capability[] = [];
  if (Array.isArray(response.lanes)) {
    response.lanes.forEach(lane => {
      // Some lanes might contain business capabilities
      if (lane.label === 'Business Capabilities' && Array.isArray(lane.stages)) {
        lane.stages.forEach(stageWithCapabilities => {
          // Find matching stage
          const matchingStage = transformedValueStream.stages.find(s => 
            s.label === stageWithCapabilities.label || s.id === stageWithCapabilities.id);
          
          // Extract capabilities if items exists
          if (Array.isArray(stageWithCapabilities.items)) {
            const stageCapabilities = stageWithCapabilities.items.map(cap => {
              // Clean up capability name - extract only the label without extra technical info
              const cleanName = cap.label ? cap.label.split('-')[0].trim() : '';
              
              // Remove any stage name that might be in the capability label
              const stageName = stageWithCapabilities.label || '';
              let finalName = cleanName;
              
              // If the capability name contains the stage name, remove it
              if (stageName && finalName.includes(stageName)) {
                finalName = finalName.replace(stageName, '').trim();
              }
              
              return {
                id: cap.id || '',
                // Use only the clean name without technical details or stage name
                name: finalName,
                timeDimension: '',
                type: 'capability'
              };
            });
            
            // Add to matching stage if found
            if (matchingStage) {
              matchingStage.capabilities = stageCapabilities;
            }
            
            // Also add to flat list of capabilities
            stageWithCapabilities.items.forEach(cap => {
              // Extract the clean capability name
              const cleanName = cap.label ? cap.label.split('-')[0].trim() : '';
              
              // Remove any stage name that might be in the capability label
              const stageName = stageWithCapabilities.label || '';
              let finalLabel = cleanName;
              
              // If the capability name contains the stage name, remove it
              if (stageName && finalLabel.includes(stageName)) {
                finalLabel = finalLabel.replace(stageName, '').trim();
              }
              
              allCapabilities.push({
                id: cap.id || '',
                label: finalLabel,
                description: cap.description || '',
                stageId: stageWithCapabilities.id || '',
                stageName: stageWithCapabilities.label || '',
                // Add default performance metrics
                timePercentage: 0,
                volumePercentage: 0,
                qualityPercentage: 0,
                sentimentPercentage: 0,
                costPercentage: 0,
                riskPercentage: 0,
                healthPercentage: 0
              });
            });
          }
        });
      }
    });
  }
  
  // If we found capabilities through lanes, use them
  if (allCapabilities.length > 0) {
    transformedValueStream.capabilities = allCapabilities;
  }
  // If traditional capabilities array exists, use it
  else if (Array.isArray(response.capabilities)) {
    transformedValueStream.capabilities = response.capabilities.map(cap => ({
      id: cap.id || '',
      label: cap.label || '',
      description: cap.description || '',
      stageId: cap.stageId || '',
      stageName: cap.stageName || '',
      // Add default performance metrics
      timePercentage: 0,
      volumePercentage: 0,
      qualityPercentage: 0,
      sentimentPercentage: 0,
      costPercentage: 0,
      riskPercentage: 0,
      healthPercentage: 0
    }));
  } else {
    console.warn('No capabilities array found in the response:', response);
  }

  console.debug('Transformed value stream:', {
    stages: transformedValueStream.stages.length,
    capabilities: transformedValueStream.capabilities.length
  });
  
  return transformedValueStream;
}
